import { DocContent } from '../docsData';

const doc: DocContent = {
  title: 'Dashboards',
  content: `
Pliable's Dashboards feature unifies multiple analyses, pipeline data, and filters into a single view, enabling teams to monitor key metrics and explore data contextually — without switching tools. Built into the dev/prod publishing workflow, dashboards ensure consistency, automate filters, and support advanced analytics like LTV.

---

## Overview

Dashboards allow you to:

- **Combine Components**: Blend nodes, analyses, filters, and markdown into one screen.
- **Sync Filters**: Apply static or dynamic filters globally — all components update dynamically.
- **Customize Layout**: Drag-and-drop components and add contextual notes.

---

## Creating a Dashboard

1. Click **"New Dashboard"**:
   - Name your dashboard (e.g., "Monthly Performance").
   - Add a description to clarify its purpose.

2. **Add Components**:
   - **Analyses**: Select saved reports (e.g., "Monthly LTV").
   - **Pipeline Nodes**: Pull raw data from sources like Snowflake or CSVs.
   - **Filters**: Add dimension or date filters to control all components.

3. **Arrange Layout**: Drag and resize components to prioritize critical metrics.

---

## Adding Components

### 1. Node Component

Display raw or cleaned data from pipeline nodes (e.g., Salesforce contacts, customer tables).

1. Click **Add Component > Node**.
2. **Select Node**: Choose from available nodes (e.g., salesforce_contacts - Cleaned).
3. **Date Dimension**: Map date columns (e.g., Entry vs. Database Date) for time-based filtering.

### 2. Analysis Component

Embed pre-configured analyses (e.g., LTV charts, revenue trends).

1. Click **Add Component > Analysis**.
2. **Select Analysis**: Pick a saved analysis from the dropdown.
3. **Refresh**: Click to update data if the source analysis changes.

### 3. Filter Component

Apply filters to control data across components.

#### Static Filter

Manually filter nodes or static analyses:

1. Click **Add Component > Filter > Static Filter**.
2. **Select Columns**: Choose which columns to apply the filter to (e.g., Product Name).
3. **Allow Multiple Selections**: Toggle to let users pick multiple filter values.

#### Automatic Dimension Filter

Dynamically filter analyses using Semantic Layer dimensions:

1. Click **Add Component > Filter > Automatic Dimension Filter**.
2. **Select Column**: Choose a dimension (e.g., Region).
3. **Dynamic List**: Pliable auto-populates filter options based on the selected column's values.

### 4. Markdown Component

Add text, headers, or descriptions to contextualize dashboard data.

1. Click **Add Component > Markdown**.
2. **Write Content**: Use markdown syntax for formatting.
   - Example: \`## Key Insights\` followed by bullet points.

---

## Configuring Filters

- **Global Filters**: Automatically apply to all compatible components.
- **Override per Component**: Adjust filters for individual analyses/nodes.
- **Dynamic Lists**: For Automatic Dimension Filters, Pliable pulls live values from the Semantic Layer.

---

## Example Workflow: Product Performance Dashboard

1. **Add Node Component**: Show raw sales data from salesforce_contacts - Cleaned.
2. **Add Analysis Component**: Embed a saved "Monthly Revenue by Product" line chart.
3. **Add Automatic Dimension Filter**: Let users filter by Product Name (dynamic list).
4. **Add Markdown**: Explain methodology: "Data reflects Q3 2024 sales."

---

## FAQs

**How do I map date dimensions for node components?**
Select the node, then choose between Entry (source timestamp) or Database Date (processed timestamp).

**Can I filter static analyses?**
Yes — use Static Filters and select the relevant columns.

**Why can't I see certain columns in my Automatic Filter?**
Ensure the column is flagged as a dimension in the Semantic Layer.

---

## Limitations

- **Static Analyses**: Require manual column mapping for date ranges.
- **Markdown**: No dynamic variables (e.g., live metrics) supported.
`,
};

export default doc;
